import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Light/dark theme for the authenticated control plane. The palette already exists as two token sets
 * in `theme.css` (`:root[data-theme="dark"]` and `:root[data-theme="light"]`); this module just owns
 * the SELECTED theme: which `data-theme` is on `<html>`. Every screen reads the tokens, so a switch
 * reflows the whole app (the Chart component also observes `data-theme` and rebuilds its canvas).
 *
 * The choice persists in `localStorage` under `oa-theme`; with nothing saved we follow the OS via
 * `prefers-color-scheme`. The initial value is applied eagerly (see {@link initTheme}, called from
 * `main.tsx` before React mounts) so there is no dark→light flash on load. The login page is
 * deliberately navy and theme-independent, so this only governs the signed-in shell.
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "oa-theme";

function systemPrefersDark(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** The persisted choice, or the OS preference when none is stored. */
export function resolveInitialTheme(): Theme {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    // localStorage can throw (private mode / disabled); fall back to the OS preference.
  }
  return systemPrefersDark() ? "dark" : "light";
}

/** Apply a theme to the document: `data-theme` on <html> and the matching `color-scheme` meta. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  const meta = document.querySelector('meta[name="color-scheme"]');
  if (meta !== null) meta.setAttribute("content", theme === "dark" ? "dark light" : "light dark");
}

/** Eagerly apply the initial theme before React mounts (no flash). Called from `main.tsx`. */
export function initTheme(): Theme {
  const theme = resolveInitialTheme();
  applyTheme(theme);
  return theme;
}

interface ThemeContextValue {
  readonly theme: Theme;
  readonly setTheme: (theme: Theme) => void;
  readonly toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider(props: { readonly children: ReactNode }): ReactNode {
  const [theme, setThemeState] = useState<Theme>(() => resolveInitialTheme());

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    applyTheme(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persisting is best-effort; the in-memory choice still applies for this session.
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const next: Theme = current === "dark" ? "light" : "dark";
      applyTheme(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* best-effort */
      }
      return next;
    });
  }, []);

  // Follow OS changes ONLY while the operator has not made an explicit choice.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (): void => {
      let hasChoice = false;
      try {
        hasChoice = window.localStorage.getItem(STORAGE_KEY) !== null;
      } catch {
        hasChoice = false;
      }
      if (!hasChoice) {
        const next: Theme = mql.matches ? "dark" : "light";
        setThemeState(next);
        applyTheme(next);
      }
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{props.children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
